<template>
    <div :id="`segmentException${adUnit}`" class="segment-exception">
        <ExceptionHeader
            :adUnit="adUnit"
            :isActive="isActive"
            @update:isActive="$emit('update:isActive', $event)"
        />
        <OpacityOverlay :show="!isActive">
            <Table
                :columns='columns'
                :rows='rows'
                :sortBy="sortBy"
                :sortDirection="sortDirection"
                :pagination="false"
                @sortChanged="handleSortChanged"
            >
                <template v-slot:row-cell="{ rowCell, columnIndex }">
                    <RewardColumn
                        :adUnit="adUnit"
                        v-if="columns[columnIndex].id === '1'"
                        :placement="rowCell.data.placement"
                        :exception="rowCell.data.exception"
                        @edit="handleEditReward(rowCell.data)"
                    />
                    <CappingPacingColumn
                        :adUnit="adUnit"
                        v-if="columns[columnIndex].id === '2'"
                        :placement="rowCell.data.placement"
                        :exception="rowCell.data.exception"
                        @edit="handleEditCapping(rowCell.data)"
                        @add="handleAddCapping(rowCell.data)"
                    />
                    <div v-else>
                        <span :class="{'font-4': columnIndex === sortedIndex}">
                            {{ rowCell.text }}
                        </span>
                    </div>
                </template>
            </Table>
        </OpacityOverlay>
    </div>
</template>

<script>
import Table from '@/components/table/Table.vue';
import CappingPacingColumn from '@/pages/monetize/segments/segmentForm/columns/CappingPacingColumn.vue';
import RewardColumn from '@/pages/monetize/segments/segmentForm/columns/RewardColumn.vue';
import ExceptionHeader from '@/pages/monetize/segments/segmentForm/ExceptionHeader.vue';
import OpacityOverlay from '@/pages/monetize/segments/segmentForm/OpacityOverlay.vue';
import { sortBy } from 'lodash';

export default {
    name: 'SegmentException',
    components: {
        OpacityOverlay,
        ExceptionHeader,
        RewardColumn,
        CappingPacingColumn,
        Table
    },
    props: {
        adUnit: {
            type: String,
            required: true
        },
        isActive: {
            type: Boolean,
            default: true
        },
        placements: {
            type: Array,
            default: () => []
        },
        exceptions: {
            type: Array,
            default: () => []
        },
        supportsReward: {
            type: Boolean,
            default: false
        }
    },
    computed: {
        columns() {
            return [
                { id: '0', text: 'Placement Name' },
                ...(this.supportsReward ? [{ id: '1', text: 'Reward', noSort: true }] : []),
                { id: '2', text: 'Capping & Pacing', noSort: true }
            ];
        },
        rows() {
            // sort by placement id initially
            const placements = sortBy(this.placements, 'id');

            return placements.map((placement, index) => {
                const found = this.exceptions.find((e) => e.placementId === placement.id);
                const data = { placement, exception: found || {} };
                return [
                    { id: `${index}0`, text: placement.name, value: placement.name },
                    ...(this.supportsReward ? [{ id: `${index}1`, data }] : []),
                    { id: `${index}2`, data }
                ];
            });
        },
        sortedIndex() {
            return this.columns.findIndex((col) => col.id === this.sortBy);
        }
    },
    methods: {
        handleSortChanged(sortId, sortDirection) {
            this.sortBy = sortId;
            this.sortDirection = sortDirection;
        },
        handleEditReward(data) {
            this.$emit('editReward', this.getEventData(data));
        },
        handleEditCapping(data) {
            this.$emit('editCapping', this.getEventData(data));
        },
        handleAddCapping(data) {
            this.$emit('addCapping', this.getEventData(data));
        },
        getEventData({ placement, exception }) {
            return { adUnit: this.adUnit, placement, exception };
        }
    },
    data() {
        return {
            sortBy: '',
            sortDirection: 'desc'
        };
    }
};
</script>

<style lang="scss" scoped>
@import "~styles/styleGuide.scss";

.segment-exception {
    border: $border;
    border-radius: $borderRadius;
    margin-bottom: 21px;
}

header {
    border-bottom: $border;
    margin-bottom: 10px;
}

/deep/ table {
    table-layout: fixed;
    thead th {
        padding-bottom: 7px;
        &:last-child {
            width: 423px;
        }
    }
}
</style>
